import {
  deriveSeatPriceZonesFromSeats,
  getSeatPriceZonesForPassengersLeg,
  getSeatPriceZonesFromSource,
} from 'utils/seatPricing';

/**
 * Normalize zone reference for deduplication (camelCase or legacy snake_case from API).
 * @param {Object} zone - Zone item from API or store.
 * @returns {string} Trimmed reference or empty string.
 */
const getZoneReferenceKey = (zone) =>
  String(zone?.seatFareReference ?? zone?.seat_fare_reference ?? '').trim();

/**
 * Concatenate two zone lists and keep first occurrence per seat fare reference.
 * @param {Array<Object>} listA - First zone list.
 * @param {Array<Object>} listB - Second zone list.
 * @returns {Array<Object>} Merged deduplicated zones.
 */
export const mergeSeatPriceZonesUniqueByReference = (listA = [], listB = []) => {
  return [...listA, ...listB].filter((zone, index, zones) => {
    const ref = getZoneReferenceKey(zone);
    if (!ref) return false;
    return zones.findIndex((other) => getZoneReferenceKey(other) === ref) === index;
  });
};

/**
 * Resolve which seat_price_zones array to use for checkout pricing zone map.
 * Priority: purchase leg merged with seatsBus, then reservation + seatsBus entry, then seats derived from pre-checkout selection.
 *
 * @param {object} params - Input parameters.
 * @param {object} params.purchaseLeg - `purchase.departs` or `purchase.returns` (plain).
 * @param {object} params.reservation - Reservation slice for this trip leg.
 * @param {object} params.seatsBus - Redux seatsBus map (plain).
 * @param {string} params.tripSlug - Trip slug key in seatsBus.
 * @param {Array<Object>} [params.selectedSeatsBeforeCheckout] - Optional pre-checkout seats for fallback zones.
 * @returns {Array<Object>} Zones array for {@link buildZoneMap}.
 */
export const resolveSeatPriceZonesForTripZoneMap = ({
  purchaseLeg = {},
  reservation = {},
  seatsBus = {},
  tripSlug,
  selectedSeatsBeforeCheckout = [],
}) => {
  const seatPriceZonesFromPurchaseLeg = getSeatPriceZonesForPassengersLeg(purchaseLeg, seatsBus);
  const seatPriceZonesFromReservation = getSeatPriceZonesForPassengersLeg(reservation, seatsBus);
  const seatPriceZonesFromSeatsBus = getSeatPriceZonesFromSource(seatsBus?.[tripSlug]);
  const resolvedSeatPriceZones = mergeSeatPriceZonesUniqueByReference(
    seatPriceZonesFromReservation,
    seatPriceZonesFromSeatsBus,
  );
  const fallbackSeatPriceZones =
    Array.isArray(selectedSeatsBeforeCheckout) && selectedSeatsBeforeCheckout.length > 0
      ? deriveSeatPriceZonesFromSeats(selectedSeatsBeforeCheckout)
      : [];

  if (seatPriceZonesFromPurchaseLeg.length > 0) {
    return seatPriceZonesFromPurchaseLeg;
  }
  if (resolvedSeatPriceZones.length > 0) {
    return resolvedSeatPriceZones;
  }
  return fallbackSeatPriceZones;
};
